<script setup>
import { computed, inject } from 'vue';
import { styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// In the editor an empty gallery draws six hatched slots, so a manager
// sees where the photos go. On the live page a slot with no photo is
// dropped, and a gallery with none is not drawn at all, heading
// included (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const images = computed(() => (Array.isArray(props.config.images) ? props.config.images : [])
    .filter((im) => im && (editing || im.url)));
const placeholderCount = computed(() => (images.value.length || !editing ? 0 : 6));
</script>

<template>
    <section v-if="images.length || editing"
             :id="config.anchor_id || null"
             class="th-block th-sec th-gallery"
             :style="styleVars(config)">
        <div class="th-wrap">
            <div v-if="config.eyebrow || config.heading || config.tag_line" class="th-head">
                <div>
                    <p v-if="config.eyebrow" class="th-eyebrow">{{ config.eyebrow }}</p>
                    <h2 v-if="config.heading" class="th-h2">{{ config.heading }}</h2>
                </div>
                <span v-if="config.tag_line" class="th-mono th-gallery__tagline">{{ config.tag_line }}</span>
            </div>

            <div class="th-grid th-gallery__grid">
                <figure v-for="(img, i) in images" :key="i" class="th-gallery__item">
                    <div class="th-media th-gallery__media">
                        <img v-if="img.url" :src="img.url" :alt="img.alt || ''" loading="lazy">
                        <span v-else class="th-media__label">Gallery photo</span>
                    </div>
                    <figcaption v-if="img.caption" class="th-gallery__caption">{{ img.caption }}</figcaption>
                </figure>
                <div v-for="n in placeholderCount" :key="`ph-${n}`" class="th-media th-gallery__media">
                    <span class="th-media__label">Gallery photo</span>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-gallery__grid { --th-min: 260px; --th-gap: 12px; }
.th-gallery__tagline { font-size: 13px; font-weight: 500; color: var(--th-muted); }
.th-gallery__item { margin: 0; display: flex; flex-direction: column; gap: 8px; }
.th-gallery__media { aspect-ratio: 4 / 3; }
.th-gallery__caption { font-size: 14px; color: var(--text-sub, var(--th-muted)); }
</style>
